<template>
    <div id="entity-task-queue">
        <div v-if="currentRow" class="task-item-container">
            <div class="task-header">
                <SearchResultAvatar v-if="currentEntity" :entity="currentEntity" size="medium" />
                <h2 class="header" v-if="currentEntity">{{ title }} - Tasks</h2>
                <span class="material-icons" @click="closeDrawer">close</span>
            </div>
            <hr />
            <div class="task-item-navigation">
                <i class="material-icons" @click="navigatePrevious">arrow_back</i>
                <i class="material-icons" @click="navigateQueue">settings_backup_restore</i>
                <i class="material-icons" @click="navigateNext">arrow_forward</i>
            </div>
            <hr />
            <TaskItem :item="item" />
        </div>

        <div class="task-item-container" v-else>
            <div class="task-header">
                <SearchResultAvatar v-if="currentEntity" :entity="currentEntity" size="medium" />
                <h2 class="header" v-if="currentEntity">{{ title }} - Tasks</h2>
                <span class="material-icons" @click="closeDrawer">close</span>
            </div>

            <TasksTable
                class="tasks-table"
                :tasks="filteredTasks"
                :categories="categories"
                :selectedCategories.sync="selectedCategories"
                :showEntity="false"
                :updating="updating"
                @taskSelected="navigateTo"
                @update="update"
            />
        </div>
    </div>
</template>
<script setup>
import SearchResultAvatar from '@/components/Search/avatar'
import TaskService from '@/services/manage/taskService'
import actions from '@/store/actions'
import TasksTable from '@/pages/manage/dashboard/TasksTable.vue'
import TaskItem from './task-item.vue'
import entityTypes from '@/utils/entityTypes'
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import { asyncComputed } from '@/utils/asyncComputed'

const props = defineProps({
    item: {
        type: Object,
        required: false,
        default() {
            return null
        },
    },
})

const selectedCategories = ref([])

const bus = inject('bus')

onMounted(() => bus.subscribe('task-completed', taskCompleted))
onUnmounted(() => bus.unsubscribe('task-completed', taskCompleted))

const store = inject('store')

const currentEntity = computed(() => store.getters.currentEntity)

const tasks = asyncComputed(async () => {
    if (!currentEntity.value) {
        return []
    }

    const tasks = await TaskService.listByEntityTypeAndEntityId(currentEntity.value.type, currentEntity.value.id)
    tasks.forEach(t => {
        t.entity = currentEntity.value
    })
    return tasks
}, [])

const categories = computed(() => [...new Set(tasks.value.map(t => t.category))])

const currentRow = computed({
    get() {
        return props.item
    },
    set(item) {
        store.dispatch(actions.showTaskQueue, item)
    },
})

const updating = computed(() => tasks.updating)

const entityType = computed(() => currentEntity.value.type)

const filteredTasks = computed(() => {
    if (selectedCategories.value.length === 0) {
        return tasks.value
    }

    return tasks.value.filter(task => selectedCategories.value.includes(task.category))
})

const title = computed(() => {
    switch (entityType.value) {
        case entityTypes.executive:
            return currentEntity.value.colloquialName
        case entityTypes.company:
            return currentEntity.value.name
        default:
            return ''
    }
})

function navigateTo(task) {
    currentRow.value = task
}

function navigatePrevious() {
    currentRow.value = tasks.value[tasks.value.indexOf(currentRow.value) - 1] || null
}

function navigateNext() {
    currentRow.value = tasks.value[tasks.value.indexOf(currentRow.value) + 1] || null
}

function navigateQueue() {
    currentRow.value = null
}

function taskCompleted(item) {
    let index = tasks.value.findIndex(t => t.id === item.id)
    if (index > -1) {
        tasks.value.splice(index, 1)
        navigateTo(tasks.value[index] || null)
    }
}

function closeDrawer() {
    store.dispatch(actions.clearRightDrawer)
}

function update() {
    tasks.update()
}
</script>

<style>
#entity-task-queue {
    height: 100%;

    h1 {
        margin-bottom: 16px;
    }

    .task-item-navigation {
        display: grid;
        grid-template-columns: repeat(3, 1fr);

        padding: 8px 16px;
        user-select: none;

        * {
            cursor: pointer;
            text-align: center;

            &:first-child {
                text-align: left;
            }

            &:last-child {
                text-align: right;
            }
        }
    }

    .task-item-container {
        display: flex;
        flex-direction: column;
        height: 100%;

        .task-item {
            height: 1%;
            flex-grow: 1;
        }
    }

    .task-header {
        display: flex;
        flex-direction: row;
        flex-shrink: 1;
        justify-content: space-between;
        width: 100%;
        margin: 0;
        background-color: #cc0000;
        padding: 20px 24px;

        h2 {
            color: var(--color-primary-100);
            line-height: 48px;
            flex-grow: 1;
        }

        .material-icons {
            color: var(--color-primary-100);
            cursor: pointer;
            flex-shrink: 1;
        }
    }

    .tasks-table {
        padding-right: 24px;
    }
}
</style>
